import type { AdUnit } from '@weather-story/shared-types';
import { ExternalLink, Megaphone } from 'lucide-react';
import { useRef, useEffect, useState } from 'react';

const useAdDebug = () =>
  useState(
    () =>
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('addebug') === '1'
  )[0];

interface SponsoredStoryCardProps {
  ad: AdUnit;
  onImpression: (id: string) => void;
  onClick: (id: string) => void;
}

export function SponsoredStoryCard({ ad, onImpression, onClick }: SponsoredStoryCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const hasTracked = useRef(false);
  const isDebug = useAdDebug();

  useEffect(() => {
    const el = ref.current;
    if (!el || hasTracked.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTracked.current) {
          hasTracked.current = true;
          onImpression(ad.id);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ad.id, onImpression]);

  const handleCtaClick = () => {
    onClick(ad.id);
  };

  const debugBorder = isDebug
    ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-gray-900 relative'
    : '';

  return (
    <div ref={ref} className={`story-card p-4 ${debugBorder}`}>
      {isDebug && (
        <div className="absolute -top-3 left-3 bg-amber-500 text-black text-[9px] font-mono font-bold px-2 py-0.5 rounded-full z-10">
          Sponsored · id:{ad.id.slice(0, 8)} · partner:{ad.partner.companyName}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-gray-300 bg-gradient-to-r from-gray-600 to-gray-700">
          <Megaphone className="h-3 w-3" />
          Sponsored
        </span>
        <span className="text-xs text-gray-500">{ad.partner.companyName}</span>
      </div>

      {/* Image */}
      {ad.imageUrl && (
        <div className="mb-3 rounded-lg overflow-hidden">
          <img
            src={ad.imageUrl}
            alt={ad.title}
            className="w-full h-32 object-cover"
            loading="lazy"
          />
        </div>
      )}

      {/* Content */}
      <h3 className="font-display font-semibold text-gray-100 text-base mb-2">{ad.title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{ad.message}</p>

      {/* CTA Footer */}
      {ad.ctaText && ad.ctaUrl && (
        <div className="flex items-center justify-end mt-4 pt-4 border-t border-gray-700">
          <a
            href={ad.ctaUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={handleCtaClick}
            className="inline-flex items-center gap-1.5 bg-primary-600 hover:bg-primary-500 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors"
          >
            {ad.ctaText}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      )}
    </div>
  );
}
